import type { ReactionTotals } from '../bindings/backend/declarations/backend.did';

export const REACTION_RANKING_REFRESH_MS = 60_000;
export const REACTION_BATCH_SIZE = 200;
const CONCURRENCY = 3;
export const REACTIONS_UPDATED_EVENT = 'soonscreener:reactions-updated';
export interface ReactionRanking {
  totals: Map<string, ReactionTotals | null>;
  covered: number; total: number; complete: boolean; testMode: boolean; asOf: number;
}
const byId = (a: string, b: string) => a < b ? -1 : a > b ? 1 : 0;
const isNat = (value: unknown): value is bigint => typeof value === 'bigint' && value >= 0n;

/** Read every requested factory market in bounded batches; an RPC failure is unknown, never zero. */
export async function readReactionRanking(markets: string[], read: (markets: string[]) => Promise<ReactionTotals[]>, allowTestMode = false): Promise<ReactionRanking> {
  const ids = [...new Set(markets)].sort(byId);
  const totals = new Map<string, ReactionTotals | null>(ids.map(id => [id, null]));
  const batches = Array.from({ length: Math.ceil(ids.length / REACTION_BATCH_SIZE) }, (_, i) => ids.slice(i * REACTION_BATCH_SIZE, (i + 1) * REACTION_BATCH_SIZE));
  let next = 0, testMode = false;
  await Promise.all(Array.from({ length: Math.min(CONCURRENCY, batches.length) }, async () => {
    while (next < batches.length) {
      const requested = batches[next++];
      try {
        const rows = await read(requested), expected = new Set(requested), seen = new Set<string>();
        // A truncated, duplicated or unrelated result cannot establish coverage.
        if (rows.length !== requested.length) throw Error('Incomplete reaction count batch.');
        for (const row of rows) {
          const id = row.market.toText();
          if (!expected.has(id) || seen.has(id)) throw Error('Unexpected reaction count market.');
          seen.add(id);
          if (typeof row.test_mode !== 'boolean' || row.test_mode && !allowTestMode ||
              ![row.rocket, row.fire, row.poop, row.flag, row.total].every(isNat) ||
              !Array.isArray(row.updated_at) || row.updated_at.length > 1 || !row.updated_at.every(isNat) ||
              row.rocket + row.fire + row.poop + row.flag !== row.total) throw Error('Invalid reaction totals.');
        }
        for (const row of rows) { totals.set(row.market.toText(), row); testMode ||= row.test_mode; }
      } catch { /* This batch remains unknown; successful batches retain their exact counts. */ }
    }
  }));
  const covered = [...totals.values()].filter(value => value !== null).length;
  return { totals, covered, total: ids.length, complete: covered === ids.length, testMode, asOf: Date.now() };
}

/** Known zero sorts above unknown in either direction. Totals never pass through Number. */
export function compareReactionTotals(a: string, b: string, totals: ReadonlyMap<string, ReactionTotals | null> | undefined, direction = -1): number {
  const first = totals?.get(a)?.total, second = totals?.get(b)?.total;
  if (first === undefined || second === undefined) return first === second ? byId(a, b) : first === undefined ? 1 : -1;
  return (first === second ? 0 : direction * (first > second ? 1 : -1)) || byId(a, b);
}

let cached: { key: string; expires: number; pending: Promise<ReactionRanking> } | undefined;
export function invalidateReactionRanking() { cached = undefined; }
// A payment usually happens on a token page while Discover is unmounted. Keep
// invalidation alive so returning to Trending cannot reuse a pre-payment cache.
if (typeof window !== 'undefined') window.addEventListener(REACTIONS_UPDATED_EVENT, invalidateReactionRanking);
export async function loadReactionRanking(markets: string[]): Promise<ReactionRanking> {
  const ids = [...new Set(markets)].sort(byId), key = ids.join(',');
  if (cached?.key === key && cached.expires > Date.now()) return cached.pending;
  const pending = (async () => {
    const [{ backend }, { Principal }] = await Promise.all([import('./backend'), import('@icp-sdk/core/principal')]);
    const service = await backend();
    return readReactionRanking(ids, requested => service.reaction_totals_batch(requested.map(id => Principal.fromText(id))), import.meta.env.VITE_DFX_NETWORK === 'local');
  })();
  const entry = { key, expires: Date.now() + REACTION_RANKING_REFRESH_MS, pending };
  cached = entry;
  try { return await pending; }
  catch (error) { if (cached === entry) cached = undefined; throw error; }
}
